import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Drawer,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  OpenInNew as ExternalLinkIcon,
  TrendingUp as PositiveIcon,
  TrendingDown as NegativeIcon,
  Remove as NeutralIcon,
  Chat as ChatIcon,
  Close as CloseIcon,
  ShoppingCart as BuyIcon,
  Sell as SellIcon,
  CardGiftcard as GiftIcon,
  SwapHoriz as OtherIcon,
} from '@mui/icons-material';
import { companyApi } from '../../services/api';
import AISidebar from './AISidebar';
import RecordDetailModal from './RecordDetailModal';

const SIDEBAR_WIDTH = 320;

const eventTypeColors = {
  FDA_CRL: '#f44336',
  FDA_APPROVAL: '#4caf50',
  FDA_BREAKTHROUGH: '#2196f3',
  TRIAL_RESULT_POSITIVE: '#4caf50',
  TRIAL_RESULT_NEGATIVE: '#f44336',
  DEAL_ACQUISITION: '#ff9800',
  MGMT_CEO: '#9c27b0',
  SEC_10K: '#607d8b',
  SEC_10Q: '#607d8b',
  SEC_8K: '#795548',
  default: '#757575',
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '16px 0' }}>
      {value === index && children}
    </div>
  );
}

function CompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [company, setCompany] = useState(null);
  const [events, setEvents] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [trials, setTrials] = useState([]);
  const [financials, setFinancials] = useState([]);
  const [insider, setInsider] = useState({ transactions: [], summary: {}, chart_data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Sidebar state (for mobile)
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState(null);

  useEffect(() => {
    loadCompanyData();
  }, [companyId]);

  const loadCompanyData = async () => {
    try {
      setLoading(true);
      const [companyRes, eventsRes, pipelineRes, trialsRes, financialsRes, insiderRes] = await Promise.all([
        companyApi.getCompany(companyId),
        companyApi.getCompanyEvents(companyId, { limit: 20 }),
        companyApi.getCompanyPipeline(companyId),
        companyApi.getCompanyTrials(companyId),
        companyApi.getCompanyFinancials(companyId, { periods: 8 }),
        companyApi.getCompanyInsider(companyId, { days_back: 365 }),
      ]);

      setCompany(companyRes.data);
      setEvents(eventsRes.data.events || []);
      setPipeline(pipelineRes.data.pipeline || []);
      setTrials(trialsRes.data.trials || []);
      setFinancials(financialsRes.data.financials || []);
      setInsider(insiderRes.data || { transactions: [], summary: {}, chart_data: [] });
    } catch (err) {
      setError('Failed to load company data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (record, type) => {
    setSelectedRecord(record);
    setSelectedRecordType(type);
    setModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}B`;
    return `$${value.toFixed(0)}M`;
  };

  const getEventColor = (eventType) => {
    return eventTypeColors[eventType] || eventTypeColors.default;
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'POSITIVE':
        return <PositiveIcon sx={{ color: '#4caf50', fontSize: 18 }} />;
      case 'NEGATIVE':
        return <NegativeIcon sx={{ color: '#f44336', fontSize: 18 }} />;
      default:
        return <NeutralIcon sx={{ color: '#757575', fontSize: 18 }} />;
    }
  };

  const getTransactionTypeInfo = (type) => {
    switch (type) {
      case 'BUY':
        return { icon: <BuyIcon sx={{ fontSize: 18 }} />, color: '#4caf50', label: 'Buy' };
      case 'SELL':
        return { icon: <SellIcon sx={{ fontSize: 18 }} />, color: '#f44336', label: 'Sell' };
      case 'GIFT':
        return { icon: <GiftIcon sx={{ fontSize: 18 }} />, color: '#ff9800', label: 'Gift' };
      default:
        return { icon: <OtherIcon sx={{ fontSize: 18 }} />, color: '#757575', label: 'Other' };
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!company) {
    return <Alert severity="warning">Company not found.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)' }}>
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/company')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              {company.company_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {company.ticker && (
                <Chip label={company.ticker} color="primary" size="small" />
              )}
              {company.sector && (
                <Chip label={company.sector} variant="outlined" size="small" />
              )}
              {company.exchange && (
                <Typography variant="body2" color="text.secondary">
                  {company.exchange}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Mobile AI Button */}
          {isMobile && (
            <IconButton
              color="primary"
              onClick={() => setSidebarOpen(true)}
              sx={{ bgcolor: 'primary.light', color: 'white' }}
            >
              <ChatIcon />
            </IconButton>
          )}
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h4" fontWeight="bold">
                  {events.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Events
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h4" fontWeight="bold">
                  {pipeline.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pipeline Drugs
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h4" fontWeight="bold">
                  {trials.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clinical Trials
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Last Event
                </Typography>
                <Typography variant="h6">
                  {formatDate(events[0]?.event_date)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={`Events (${events.length})`} />
            <Tab label={`Pipeline (${pipeline.length})`} />
            <Tab label={`Trials (${trials.length})`} />
            <Tab label={`Financials (${financials.length})`} />
            <Tab label={`Insider (${insider.transactions?.length || 0})`} />
          </Tabs>

          {/* Events Tab */}
          <TabPanel value={activeTab} index={0}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Drug</TableCell>
                    <TableCell>Sentiment</TableCell>
                    <TableCell>Summary</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No events recorded.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map((event) => (
                      <TableRow
                        key={event.event_id}
                        hover
                        onClick={() => handleRowClick(event, 'event')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{formatDate(event.event_date)}</TableCell>
                        <TableCell>
                          <Chip
                            label={event.event_type?.replace('_', ' ')}
                            size="small"
                            sx={{
                              bgcolor: getEventColor(event.event_type),
                              color: 'white',
                              fontSize: '0.7rem',
                            }}
                          />
                        </TableCell>
                        <TableCell>{event.drug_name || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getSentimentIcon(event.sentiment)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {event.summary || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {event.filing_url && (
                            <Tooltip title="View Filing">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(event.filing_url, '_blank');
                                }}
                              >
                                <ExternalLinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Pipeline Tab */}
          <TabPanel value={activeTab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Drug Name</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Indication</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>PDUFA Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipeline.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary">No pipeline data.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pipeline.map((drug) => (
                      <TableRow
                        key={drug.pipeline_id}
                        hover
                        onClick={() => handleRowClick(drug, 'pipeline')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography fontWeight="medium">{drug.drug_name}</Typography>
                          {drug.drug_name_generic && (
                            <Typography variant="caption" color="text.secondary">
                              {drug.drug_name_generic}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={drug.phase?.replace('_', ' ')}
                            size="small"
                            color={drug.phase === 'APPROVED' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {drug.indication || drug.indication_broad || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={drug.status}
                            size="small"
                            color={drug.status === 'Active' ? 'primary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(drug.pdufa_date)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Trials Tab */}
          <TabPanel value={activeTab} index={2}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>NCT Number</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enrollment</TableCell>
                    <TableCell>Completion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No clinical trials linked.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    trials.map((trial) => (
                      <TableRow
                        key={trial.nct_number}
                        hover
                        onClick={() => handleRowClick(trial, 'trial')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Chip
                            label={trial.nct_number}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {trial.brief_title}
                          </Typography>
                        </TableCell>
                        <TableCell>{trial.phase || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={trial.overall_status}
                            size="small"
                            color={
                              trial.overall_status === 'COMPLETED'
                                ? 'success'
                                : trial.overall_status === 'RECRUITING'
                                ? 'primary'
                                : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{trial.enrollment_count || '-'}</TableCell>
                        <TableCell>{formatDate(trial.primary_completion_date)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Financials Tab */}
          <TabPanel value={activeTab} index={3}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">R&D</TableCell>
                    <TableCell align="right">Net Income</TableCell>
                    <TableCell align="right">Cash</TableCell>
                    <TableCell align="right">Runway</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financials.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No financial data.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    financials.map((f) => (
                      <TableRow
                        key={f.financial_id}
                        hover
                        onClick={() => handleRowClick(f, 'financial')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography fontWeight="medium">
                            {f.period_type} {f.fiscal_year}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(f.period_end)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(f.total_revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(f.r_and_d_expense)}</TableCell>
                        <TableCell align="right">
                          <Typography color={f.net_income < 0 ? 'error' : 'success.main'}>
                            {formatCurrency(f.net_income)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(f.total_cash)}</TableCell>
                        <TableCell align="right">
                          {f.runway_months ? (
                            <Box>
                              <Typography>{Math.round(f.runway_months)} mo</Typography>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(f.runway_months / 36 * 100, 100)}
                                color={f.runway_months < 12 ? 'error' : f.runway_months < 24 ? 'warning' : 'success'}
                                sx={{ height: 4, borderRadius: 2 }}
                              />
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Insider Trading Tab */}
          <TabPanel value={activeTab} index={4}>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: '#e8f5e9' }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BuyIcon sx={{ color: '#4caf50' }} />
                      <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                        {insider.summary?.buys?.count || 0} Buys
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatValue(insider.summary?.buys?.total_value)} total
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {insider.summary?.buys?.unique_insiders || 0} unique insiders
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ bgcolor: '#ffebee' }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SellIcon sx={{ color: '#f44336' }} />
                      <Typography variant="h6" fontWeight="bold" color="#c62828">
                        {insider.summary?.sells?.count || 0} Sells
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatValue(insider.summary?.sells?.total_value)} total
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {insider.summary?.sells?.unique_insiders || 0} unique insiders
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{
                  bgcolor: insider.summary?.net_sentiment === 'BULLISH' ? '#e3f2fd'
                    : insider.summary?.net_sentiment === 'BEARISH' ? '#fce4ec'
                    : '#f5f5f5'
                }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {insider.summary?.net_sentiment === 'BULLISH' ? (
                        <PositiveIcon sx={{ color: '#1976d2' }} />
                      ) : insider.summary?.net_sentiment === 'BEARISH' ? (
                        <NegativeIcon sx={{ color: '#d32f2f' }} />
                      ) : (
                        <NeutralIcon sx={{ color: '#757575' }} />
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {insider.summary?.net_sentiment || 'NEUTRAL'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Net insider sentiment
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last {insider.summary?.period_days || 180} days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Monthly Trend Chart */}
            {insider.chart_data && insider.chart_data.length > 0 && (
              <Card sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
                  Monthly Transaction Volume
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 120, gap: 0.5 }}>
                  {insider.chart_data.slice(-12).map((month, idx) => {
                    const maxValue = Math.max(...insider.chart_data.map(m => Math.max(m.buy_value || 0, m.sell_value || 0)));
                    const buyHeight = maxValue > 0 ? ((month.buy_value || 0) / maxValue) * 100 : 0;
                    const sellHeight = maxValue > 0 ? ((month.sell_value || 0) / maxValue) * 100 : 0;
                    return (
                      <Tooltip
                        key={idx}
                        title={`${month.month}: Buys ${formatValue(month.buy_value)}, Sells ${formatValue(month.sell_value)}`}
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', gap: 0.25, height: 100, alignItems: 'flex-end' }}>
                            <Box sx={{
                              width: 12,
                              height: `${buyHeight}%`,
                              bgcolor: '#4caf50',
                              borderRadius: '2px 2px 0 0',
                              minHeight: month.buy_value > 0 ? 4 : 0
                            }} />
                            <Box sx={{
                              width: 12,
                              height: `${sellHeight}%`,
                              bgcolor: '#f44336',
                              borderRadius: '2px 2px 0 0',
                              minHeight: month.sell_value > 0 ? 4 : 0
                            }} />
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.5 }}>
                            {month.month?.slice(-2)}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 1 }} />
                    <Typography variant="caption">Buys</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 1 }} />
                    <Typography variant="caption">Sells</Typography>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Transactions Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Insider</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Shares</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(!insider.transactions || insider.transactions.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary">No insider transactions recorded.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    insider.transactions.map((txn, idx) => {
                      const typeInfo = getTransactionTypeInfo(txn.transaction_type);
                      return (
                        <TableRow
                          key={txn.transaction_id || idx}
                          hover
                          onClick={() => handleRowClick(txn, 'insider')}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                          <TableCell>
                            <Typography fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
                              {txn.insider_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {txn.insider_title || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={typeInfo.icon}
                              label={typeInfo.label}
                              size="small"
                              sx={{
                                bgcolor: `${typeInfo.color}20`,
                                color: typeInfo.color,
                                '& .MuiChip-icon': { color: typeInfo.color },
                                fontSize: '0.75rem',
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">{formatNumber(txn.shares_transacted)}</TableCell>
                          <TableCell align="right">
                            {txn.price_per_share ? `$${txn.price_per_share.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight="medium"
                              color={txn.transaction_type === 'BUY' ? 'success.main' : txn.transaction_type === 'SELL' ? 'error.main' : 'text.primary'}
                            >
                              {formatValue(txn.total_value)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {txn.sec_filing_url && (
                              <Tooltip title="View SEC Filing">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(txn.sec_filing_url, '_blank');
                                  }}
                                >
                                  <ExternalLinkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>
      </Box>

      {/* Record Detail Modal */}
      <RecordDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        recordType={selectedRecordType}
        record={selectedRecord}
        companyId={companyId}
        companyName={company?.company_name}
      />
    </Box>
  );
}

export default CompanyDetail;
